import { DateRange } from 'react-day-picker'

import { PartnerHierarchyType } from 'clickNgo/entities/partner'

export enum CampaignStatus {
  Active = 'Active',
  Inactive = 'Inactive',
  Draft = 'Draft',
}

export enum CampaignFillingType {
  CallingList = 'Calling List',
  Automatic = 'Automatic',
}

export enum CampaignCallStatus {
  New = 'New',
  Done = 'Done',
  WIP = 'WIP',
  Fail = 'Fail',
}

export enum CampaignCallAction {
  None = 'None',
  SuccessfulCall = 'Successful Call',
  NoAnswer = 'No Answer',
  CallBack = 'Call Back',
  WrongNumber = 'Wrong Number',
}

export type CampaignDocument = {
  entryId: number
  campaignId: number
  fileName: string
  fileLink: string
  fileType: string
  file: Blob // Assuming the file is a Blob
  countries: string[]
  isVisible: boolean
  created?: Date
  createdBy?: string
  modified?: Date
  modifiedBy?: string
}

// Marker carried in fileType by link entries. Cannot collide with documents,
// whose fileType is always a MIME type of the uploaded file.
export const CAMPAIGN_DOCUMENT_LINK_FILE_TYPE = 'link'

export const isCampaignDocumentLink = (document: Pick<CampaignDocument, 'fileType'>): boolean =>
  document.fileType === CAMPAIGN_DOCUMENT_LINK_FILE_TYPE

// Links and documents are stored in separate tables, so their EntryIds can
// collide. Use this composite key wherever entries of both kinds share one
// list (React keys, editing-row matching).
export const getCampaignDocumentKey = (
  document: Pick<CampaignDocument, 'fileType' | 'entryId'>
): string => `${isCampaignDocumentLink(document) ? 'link' : 'file'}-${document.entryId}`

export type CampaignObjective = {
  entryId: number
  campaignId: number
  title: string
  content: string
  countries: string[]
  isVisible: boolean
}

export type CampaignCallOutcome = {
  entryId: number
  created?: Date
  createdBy?: string
  modified?: Date
  modifiedBy?: string

  campaignId: number
  partnerId: string
  locationId?: string
  callingListId: number
  isr: string

  callDate?: Date
  callStatus: CampaignCallStatus
  callAction: CampaignCallAction
  callFeedback: string

  isOpportunityIdentified: boolean
  isCallBack: boolean

  // Joined from LutCampaigns by spLutCampaignCallOutcomesGet; empty on older procedure versions.
  campaignTitle?: string
  campaignMsFormsUrl?: string
}

export type CampaignCallingList = {
  id: number
  isr: string
  partnerId: string
  locationId: string
  campaignId: number
  isVisible: boolean
  ranking: number | null
}

export type CampaignExtendedCallingList = CampaignCallingList & {
  communicationName: string
  country: string
  partnerType: string
  hierarchyType: PartnerHierarchyType
  agreements: string[]
  programs: string[]
  agentRank: string
  specialPricingFlag: string
  freqFlag: string
  isAmplifyMember: boolean
  callStatus: CampaignCallStatus
  callAction: CampaignCallAction
}

export type Campaign = {
  entryId: number
  title: string
  status: CampaignStatus
  countries: string[]
  subRoles: string[]
  fillingType: CampaignFillingType
  period: DateRange
  objectives: CampaignObjective[]
  msFormsUrl: string
  owners: string[]
  created?: Date
  createdBy?: string
  modified?: Date
  modifiedBy?: string
}

export type CampaignRating = {
  isr: string
  isrName: string
  score: number
}
